/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { configDefault } from '../../../core/config';
import { useDispatch, useSelector } from 'react-redux';
import Filter from '../../../interfaces/forms/Filter';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import Pager from '../../../components/Adescom/Pager';
import { fractionServiceCodeConstants, serviceCodeReducerType } from '../../../modules/servicecode/reducers';
import RemoveButton from '../../../components/Buttons/RemoveButton';
import { fractionsConstants } from '../../../modules/fractions/actions';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import { withStyles } from '@material-ui/core/styles';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';

interface FractionListUsedListInterface {
    classes: any;
    serviceCodeId: number;
}

const FractionListUsedList = (props: FractionListUsedListInterface): React.ReactElement => {
    const dispatch = useDispatch();

    const [filters, setFilters] = useState<Array<Filter>>([
        {
            name: 'serviceCodeId',
            value: props.serviceCodeId,
        },
    ]);
    const [checkedFractions, setCheckedFractions] = useState<Array<number>>([]);
    const [isCheckedFractionsAll, setIsCheckedFractionsAll] = useState<boolean>(false);

    const tableCellRoot = {
        root: props.classes.tableCellRootBordered,
    };
    const tableStickyHeader = {
        root: props.classes.tableStickyHeader,
    };

    const { collections } = useSelector((state: { fractionServiceCodeReducers: serviceCodeReducerType }) => {
        return state.fractionServiceCodeReducers;
    });

    const getFractionServiceCodes = useCallback((): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/fraction_service_codes', configDefault.PLATFORM_API_PREFIX)),
            fractionServiceCodeConstants.FRACTION_SERVICE_CODE_LIST,
            filters,
            [],
            (data: any) => {
                dispatch({
                    type: fractionServiceCodeConstants.FRACTION_SERVICE_CODE_LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [dispatch, filters]);

    useEffect(() => {
        getFractionServiceCodes();
    }, [getFractionServiceCodes]);

    const handleFractionCheck = (event: React.ChangeEvent<HTMLInputElement>): void => {
        let id = parseInt(event.target.name);
        let newFractionCheckedArr = [];
        if (checkedFractions.includes(id)) {
            newFractionCheckedArr = checkedFractions.filter((item: number) => item !== id);
        } else {
            newFractionCheckedArr = [...checkedFractions, id];
        }
        setCheckedFractions(newFractionCheckedArr);
    };

    const handleCheckAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newCheckedFractions: Array<number> = [];
        if (!isCheckedFractionsAll) {
            collections &&
                collections['hydra:member'].forEach((el: any) => {
                    newCheckedFractions.push(el.id);
                });
            setCheckedFractions(newCheckedFractions);
            setIsCheckedFractionsAll(true);
        } else {
            setCheckedFractions([]);
            setIsCheckedFractionsAll(false);
        }
    };

    const handleRemoveCheckedElements = (): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.deleteCall(
            FormGeneralHelper.getByUri(
                FormGeneralHelper.getPath(`/fraction_service_codes/multiple/${checkedFractions.join(',')}`, configDefault.PLATFORM_API_PREFIX)
            ),
            fractionServiceCodeConstants.FRACTION_SERVICE_CODE_EDIT,
            (data: any) => {
                getFractionServiceCodes();
                const itemLoaders = new ItemLoaders(dispatch);
                itemLoaders.getListWithCallback(
                    FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/fractions', configDefault.PLATFORM_API_PREFIX)),
                    fractionsConstants.LIST,
                    [{ name: 'noServiceCode', value: props.serviceCodeId }],
                    [],
                    (data: any) => {
                        setCheckedFractions([]);
                        setIsCheckedFractionsAll(false);
                        dispatch({
                            type: fractionsConstants.LIST,
                            collections: data,
                        });
                    },
                    {
                        propagateDataToCallback: true,
                        dispatchAfterCallback: false,
                    }
                );
            },
            {
                dispatchAfterCallback: false,
            }
        );
    };

    const paginationCallbackFunction = (uri: string | null = null): void => {
        if (!uri) {
            uri = FormGeneralHelper.getPath('/fraction_service_codes', configDefault.PLATFORM_API_PREFIX);
        }
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(uri),
            fractionServiceCodeConstants.FRACTION_SERVICE_CODE_LIST,
            filters,
            [],
            (data: any) => {
                dispatch({
                    type: fractionServiceCodeConstants.FRACTION_SERVICE_CODE_LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    };

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell classes={tableStickyHeader}>
                        <Checkbox checked={isCheckedFractionsAll} onChange={handleCheckAll} />
                        <RemoveButton
                            nonTableButton
                            disabled={checkedFractions.length === 0}
                            callback={handleRemoveCheckedElements}
                            tooltipTitle="service_code:b.remove_fraction"
                            tooltipPlacement="bottom-start"
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader}>
                        <TableHeaderContentCell textAlign="left" title="service_code:h.fractions_not_avaible" showSort={false} onlyFilter={false} />
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {collections &&
                    collections['hydra:member'].map((el: any) => {
                        return (
                            <TableRow key={el.id}>
                                <TableCell classes={tableCellRoot}>
                                    <Checkbox checked={checkedFractions.includes(el.id)} name={el.id} onChange={handleFractionCheck} />
                                </TableCell>
                                <TableCell classes={tableCellRoot}>{el.fraction.mainname}</TableCell>
                            </TableRow>
                        );
                    })}
            </TableBody>
            <TableFooter>
                <TableRow>
                    <TableCell colSpan={2}>
                        {collections && collections['hydra:view'] && (
                            <Pager callback={paginationCallbackFunction} views={collections['hydra:view']} />
                        )}
                    </TableCell>
                </TableRow>
            </TableFooter>
        </Table>
    );
};

export default withStyles(oplAppStyle)(FractionListUsedList);
